// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { ImageIcon, Loader2, Maximize, Minus, Plus, Upload, Wifi } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useStoreMapCoverageQuery } from "@/api/hooks/use-store-map-coverage";
import { useStoreDetailQuery, useUploadStoreMapMutation } from "@/api/hooks/use-stores";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { StoreMapCoverageLegend, StoreMapCoverageOverlay } from "./store-map-coverage-overlay";

interface StoreMapTabProps {
  storeName: string;
}

export function StoreMapTab({ storeName }: StoreMapTabProps) {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.storesMapEdit);
  const { data: store } = useStoreDetailQuery(storeName);
  const uploadMap = useUploadStoreMapMutation(storeName);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string | null>(null);

  // The overlay needs the image's own pixel grid, which only the browser knows
  // once the file has actually decoded.
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [showCoverage, setShowCoverage] = useState(false);
  const coverage = useStoreMapCoverageQuery(storeName);

  const mapUrl = (store as Record<string, unknown> | undefined)?.mapFileUrl as string | undefined;

  function extractFileName(url: string): string | undefined {
    try {
      const path = new URL(url, window.location.origin).pathname;
      const last = path.split("/").pop();
      return last ? decodeURIComponent(last) : undefined;
    } catch {
      return undefined;
    }
  }

  const fileName = previewName ?? (mapUrl ? extractFileName(mapUrl) : undefined);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setPreview(URL.createObjectURL(file));
    setPreviewName(file.name);

    try {
      await uploadMap.mutateAsync(file);
      toast.success(t("stores.map.uploadSuccess"));
    } catch {
      // Error toast handled by API middleware
      setPreview(null);
      setPreviewName(null);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  const displayUrl = preview ?? (mapUrl ? `${mapUrl}?token=dbf17f7ff2074009acda894a62cd08ca` : undefined);

  const MIN_SCALE = 0.5;
  const MAX_SCALE = 8;
  const ZOOM_STEP = 1.25;

  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragState = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: displayUrl is the trigger — reset the transform whenever the image changes, even though the effect body doesn't read it.
  useEffect(
    function resetTransformOnImageChange() {
      setScale(1);
      setOffset({ x: 0, y: 0 });
    },
    [displayUrl],
  );

  function clampScale(next: number) {
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, next));
  }

  function handleZoomIn() {
    setScale((s) => clampScale(s * ZOOM_STEP));
  }

  function handleZoomOut() {
    setScale((s) => {
      const next = clampScale(s / ZOOM_STEP);
      if (next === 1) setOffset({ x: 0, y: 0 });
      return next;
    });
  }

  function handleResetView() {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    dragState.current = { startX: e.clientX, startY: e.clientY, originX: offset.x, originY: offset.y };
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const drag = dragState.current;
    if (!drag) return;
    setOffset({ x: drag.originX + (e.clientX - drag.startX), y: drag.originY + (e.clientY - drag.startY) });
  }

  function handlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (dragState.current) {
      dragState.current = null;
      setIsDragging(false);
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }

  function handleWheel(e: React.WheelEvent<HTMLDivElement>) {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    setScale((s) => clampScale(s * (e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP)));
  }

  function handleImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    setNaturalSize({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight });
  }

  async function handleToggleCoverage(active: boolean) {
    setShowCoverage(active);
    // Switching off keeps the fetched points cached, so turning it back on is free.
    // The scan aggregates a store-wide window, so it is never fired on mount —
    // only here, off a deliberate click, and only when nothing is cached yet.
    if (active && !coverage.data) await coverage.refetch();
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        {fileName && <span className="font-mono text-sm text-muted-foreground truncate">{fileName}</span>}
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
        {displayUrl && (
          <div className="ml-auto">
            <FilterToggle
              active={showCoverage}
              onChange={handleToggleCoverage}
              icon={Wifi}
              label={t("stores.map.coverage.showButton")}
              loading={coverage.isFetching}
            />
          </div>
        )}
        <Button
          className={displayUrl ? undefined : "ml-auto"}
          onClick={() => fileInputRef.current?.click()}
          disabled={!canEdit || uploadMap.isPending}
        >
          {uploadMap.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {t("stores.map.uploadButton")}
        </Button>
      </div>

      {displayUrl ? (
        <div className="relative rounded-lg border bg-white overflow-hidden">
          <div
            className="h-[600px] w-full overflow-hidden touch-none select-none p-[25px]"
            style={{ cursor: isDragging ? "grabbing" : "grab" }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onWheel={handleWheel}
          >
            {/* Image and overlay share one transformed box so they pan and zoom as a
                single unit; the SVG inside letterboxes exactly like `object-contain`,
                which is what keeps the points glued to the floor plan. */}
            <div
              className="relative h-full w-full"
              style={{
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                transformOrigin: "center center",
                transition: isDragging ? "none" : "transform 120ms ease-out",
              }}
            >
              <img
                src={displayUrl}
                alt="Store map"
                draggable={false}
                className="h-full w-full object-contain"
                onLoad={handleImageLoad}
              />
              {showCoverage && coverage.data && naturalSize && (
                <StoreMapCoverageOverlay
                  points={coverage.data.points}
                  naturalWidth={naturalSize.width}
                  naturalHeight={naturalSize.height}
                  storeMask={coverage.data.storeMask}
                />
              )}
            </div>
          </div>
          <div className="absolute top-3 right-3 flex flex-col gap-1 rounded-md border bg-background/95 p-1 shadow-sm backdrop-blur">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleZoomIn}
              disabled={scale >= MAX_SCALE}
              aria-label={t("stores.map.zoomIn")}
              title={t("stores.map.zoomIn")}
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleZoomOut}
              disabled={scale <= MIN_SCALE}
              aria-label={t("stores.map.zoomOut")}
              title={t("stores.map.zoomOut")}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleResetView}
              disabled={scale === 1 && offset.x === 0 && offset.y === 0}
              aria-label={t("stores.map.resetView")}
              title={t("stores.map.resetView")}
            >
              <Maximize className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12 text-muted-foreground">
          <ImageIcon className="mb-4 h-12 w-12" />
          <p>{t("stores.map.noMap")}</p>
        </div>
      )}
      {showCoverage && coverage.data && (
        <StoreMapCoverageLegend points={coverage.data.points} thresholds={coverage.data.thresholds} />
      )}
    </div>
  );
}
